import React from "react";
import styled from "styled-components";
import Window from "../Window";
import MenuBar from "../MenuBar";
import PixelIcon from "../PixelIcon";
import { fetchHistoryPage, HISTORY_PAGE_SIZE } from "./historyApi";

// The "every game ever played" explorer: a Win95 file-manager window over
// the full game history, chronological only (a portrait, not a database —
// no search, no filters). Driven by the server's Past Games read API,
// newest first, fetched a page at a time as the pane scrolls (the folder
// will eventually hold every game the piece has ever seen).
//
// Details view only. Clicking selects; double-click (or Enter) opens
// the game's final board in its own frozen window via onOpenGame.

function formatDate(ts) {
  const d = new Date(ts);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(2);
  let h = d.getHours();
  const suffix = h < 12 ? "AM" : "PM";
  h = h % 12 || 12;
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${mm}/${dd}/${yy} ${h}:${min} ${suffix}`;
}

function formatDuration(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function outcomeLabel(outcome) {
  return outcome === "won" ? "Won" : "Lost";
}

// Patina: the past fades a little. Newest rows are crisp; year-old rows
// sit at ~60%.
function patinaOpacity(endedAt, now) {
  const ageDays = (now - endedAt) / (24 * 60 * 60 * 1000);
  return Math.max(0.6, 1 - ageDays / 900);
}

function HistoryWindow({ onClose, inactive = false, onHelpAbout, onOpenGame }) {
  const now = React.useMemo(() => Date.now(), []);
  const [selected, setSelected] = React.useState(null);
  const [records, setRecords] = React.useState([]);
  // The archive's true size (server COUNT) — the status bar's number.
  // null until the first page answers; the loaded-row count is only
  // the fallback for old servers that don't send it.
  const [archiveTotal, setArchiveTotal] = React.useState(null);
  // "loading" | "idle" | "exhausted" | "error"
  const [pageState, setPageState] = React.useState("loading");
  const paneRef = React.useRef(null);
  // In-flight guard + cursor live in a ref so scroll handlers never race.
  const pagingRef = React.useRef({ busy: false, beforeId: null, done: false });

  const loadMore = React.useCallback(async () => {
    const p = pagingRef.current;
    if (p.busy || p.done) return;
    p.busy = true;
    setPageState("loading");
    try {
      const { records: page, total } = await fetchHistoryPage({
        beforeId: p.beforeId,
      });
      if (total != null) setArchiveTotal(total);
      if (page.length > 0) p.beforeId = page[page.length - 1].gameId;
      if (page.length < HISTORY_PAGE_SIZE) p.done = true;
      setRecords((prev) => {
        // Games that ended between two fetches shift the pages; dedupe
        // by id so a row can't appear twice.
        const seen = new Set(prev.map((r) => r.gameId));
        return [...prev, ...page.filter((r) => !seen.has(r.gameId))];
      });
      setPageState(p.done ? "exhausted" : "idle");
    } catch {
      setPageState("error");
    } finally {
      p.busy = false;
    }
  }, []);

  React.useEffect(() => {
    loadMore();
  }, [loadMore]);

  // Fetch the next page when the pane scrolls near its bottom.
  const onPaneScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 300) loadMore();
  };

  const openRecord = (gameId) => {
    setSelected(gameId);
    if (onOpenGame) onOpenGame(gameId);
  };

  // Row activation that works for mouse AND touch: the first click/tap
  // selects, a second on the same row inside the double-click window
  // opens. Browsers never synthesize dblclick from a touch double-tap,
  // so the old onDoubleClick made compact Past Games rows un-openable
  // on phones; for the mouse this is behaviorally identical, since the
  // second click of a double-click arrives as its own click first.
  const lastTapRef = React.useRef({ id: null, t: 0 });
  const tapRecord = (gameId) => {
    const last = lastTapRef.current;
    const now = Date.now();
    lastTapRef.current = { id: gameId, t: now };
    if (last.id === gameId && now - last.t < 500) openRecord(gameId);
    else setSelected(gameId);
  };

  const selectedRecord =
    selected != null ? records.find((r) => r.gameId === selected) : null;

  return (
    <StyledWindow
      title="Past Games"
      icon={<PixelIcon name="folder" size={16} />}
      inactive={inactive}
      controls={["close"]}
      onControlClick={(control) => {
        if (control === "close" && onClose) onClose();
      }}
      statusFields={[
        `${archiveTotal ?? records.length} game(s)`,
        // Only a real second field — an empty string still drew its
        // sunken bevel, a little empty tray for nothing.
        ...(selectedRecord
          ? [
              `Game #${selectedRecord.gameId} — ${outcomeLabel(
                selectedRecord.outcome,
              )}`,
            ]
          : []),
      ]}
    >
      <Chrome>
        <MenuBar
          items={[
            {
              label: "File",
              items: [{ label: "Close", onClick: onClose }],
            },
            {
              label: "Help",
              items: [
                {
                  label: "About Solitaire Alone Together…",
                  onClick: onHelpAbout,
                  disabled: !onHelpAbout,
                },
              ],
            },
          ]}
        />
        <Pane ref={paneRef} onScroll={onPaneScroll}>
          <DetailsTable>
            <thead>
              <tr>
                <th>Name</th>
                <th>Result</th>
                <th>Moves</th>
                <th>Time</th>
                <th>Ended</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <Row
                  key={r.gameId}
                  data-selected={selected === r.gameId ? "true" : undefined}
                  style={{ "--patina": patinaOpacity(r.endedAt, now) }}
                  onClick={() => tapRecord(r.gameId)}
                  // Rows are selectable: give the keyboard the same
                  // power the mouse has. Explorer semantics: Space
                  // selects, Enter opens.
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === " ") {
                      e.preventDefault();
                      setSelected(r.gameId);
                    } else if (e.key === "Enter") {
                      e.preventDefault();
                      openRecord(r.gameId);
                    }
                  }}
                >
                  <td>
                    <NameCell>Game #{r.gameId}</NameCell>
                  </td>
                  <td data-outcome={r.outcome === "won" ? "won" : "lost"}>
                    {outcomeLabel(r.outcome)}
                  </td>
                  <td>{r.moves}</td>
                  <td>{formatDuration(r.durationSecs)}</td>
                  <td>{formatDate(r.endedAt)}</td>
                </Row>
              ))}
            </tbody>
          </DetailsTable>
          {/* Footer line for the archive's edge states. Quiet, not a
              spinner — this is a folder, not an app. */}
          {pageState === "loading" && records.length === 0 && (
            <EdgeLine>Reading…</EdgeLine>
          )}
          {pageState === "error" && (
            <EdgeLine
              as="button"
              type="button"
              onClick={() => {
                pagingRef.current.busy = false;
                loadMore();
              }}
            >
              Error retrieving games. Click to retry.
            </EdgeLine>
          )}
          {pageState === "exhausted" && records.length === 0 && (
            <EdgeLine>No games yet.</EdgeLine>
          )}
        </Pane>
      </Chrome>
    </StyledWindow>
  );
}

const StyledWindow = styled(Window)`
  width: 620px;
  height: 440px;
`;

const Chrome = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  & > :first-child {
    flex-shrink: 0;
  }
`;

const Pane = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  /* Keep inertial scrolling, but do not let a hard fling rubber-band the
     sticky Details header down and expose the pane behind it. */
  overscroll-behavior: none;
  background: #fff;
  border: 2px solid;
  border-color: #808080 #fff #fff #808080;
`;

const DetailsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  & th {
    position: sticky;
    top: 0;
    /* The patina rows' opacity creates stacking contexts that would
       otherwise paint over the sticky header as they scroll under it. */
    z-index: 1;
    text-align: left;
    font-weight: normal;
    background: silver;
    padding: 2px 8px;
    /* Raised button bevel, per the real Explorer (reference/explorer/
       win95desktop3.png: headers are 3D bars even though ours don't
       sort — the history stays chronological only). Left-aligned even
       for numeric columns, which is also period-correct. */
    box-shadow:
      inset -1px -1px #0a0a0a,
      inset 1px 1px #fff,
      inset -2px -2px grey,
      inset 2px 2px #dfdfdf;
    -webkit-user-select: none;
    user-select: none;
  }
  & td {
    padding: 2px 8px;
    white-space: nowrap;
    vertical-align: middle;
  }
  & td[data-outcome="won"] {
    color: #008000;
    font-weight: bold;
  }
`;

// Keep the selection wash tight around the filename, like Explorer,
// rather than filling the whole Name cell.
const NameCell = styled.span`
  display: inline-flex;
  align-items: center;
`;

const Row = styled.tr`
  opacity: var(--patina, 1);
  cursor: default;
  -webkit-user-select: none;
  user-select: none;
  /* Two quick taps open the row — don't let the browser spend the
     second one on double-tap zoom. */
  touch-action: manipulation;

  /* Win95 Details view highlights only the icon+name, never the whole
     row (reference/explorer/win95desktop3.png). */
  &[data-selected="true"] ${NameCell} {
    background: #00007b;
    color: #fff;
  }
`;

const EdgeLine = styled.p`
  margin: 0;
  padding: 8px;
  font-size: 13px;
  color: #808080;
  background: transparent;
  border: none;
  box-shadow: none;
  text-align: left;
  width: 100%;
`;

export default HistoryWindow;
